import React from 'react';
import { useFormContext } from './FormProvider';
import CustomTextInput from '../ui/CustomTextInput';
import AppDropDownPicker from '../ui/AppDropDownPicker';
import CheckBox from '../ui/CheckBox';
import CustomDateRangeInput from '../ui/CustomDateRangeInput';

interface BaseFormFieldProps {
  name: string;
  label?: string;
  required?: boolean;
}

interface TextFieldProps extends BaseFormFieldProps {
  type?: 'text' | 'email' | 'password' | 'tel';
  placeholder?: string;
  multiline?: boolean;
  numberOfLines?: number;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  tooltip?: string;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  keyboardType?: 'default' | 'email-address' | 'numeric' | 'phone-pad';
}

export const TextField: React.FC<TextFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  multiline = false,
  numberOfLines,
  leftIcon,
  rightIcon,
  tooltip,
  required,
  autoCapitalize,
  keyboardType,
}) => {
  const { values, errors, touched, setValue, setFieldTouched } = useFormContext();
  
  const fieldError = touched[name] && errors[name];
  const errorMessage = Array.isArray(fieldError) ? fieldError[0] : fieldError;

  return (
    <CustomTextInput
      placeholder={placeholder || label}
      value={values[name] || ''}
      onChangeText={(text: string) => setValue(name, text)}
      secureTextEntry={type === 'password'}
      multiline={multiline}
      leftIcon={typeof leftIcon === 'string' ? leftIcon : undefined}
      rightIcon={typeof rightIcon === 'string' ? rightIcon : undefined}
      tooltip={tooltip}
      error={errorMessage}
    />
  );
};

interface SelectFieldProps extends BaseFormFieldProps {
  placeholder?: string;
  items: Array<{ label: string; value: string | number }>;
  searchable?: boolean;
  multiple?: boolean;
  zIndex?: number;
  searchPlaceholder?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  placeholder,
  items,
  searchable = false,
  multiple = false,
  zIndex,
  searchPlaceholder,
  required,
}) => {
  const { values, errors, touched, setValue, setFieldTouched } = useFormContext();
  
  const fieldError = touched[name] && errors[name];
  const errorMessage = Array.isArray(fieldError) ? fieldError[0] : fieldError;

  return (
    <AppDropDownPicker
      placeholder={placeholder || label || 'Select...'}
      value={values[name]}
      items={items.map(item => ({ label: item.label, value: String(item.value) }))}
      onValueChange={(value: string | string[] | null) => setValue(name, value)}
      multiple={multiple}
      error={errorMessage}
    />
  );
};

interface CheckboxFieldProps extends BaseFormFieldProps {
  checkboxLabel: string;
  size?: number;
}

// CheckboxField is temporarily disabled due to CheckBox component not accepting props
// export const CheckboxField: React.FC<CheckboxFieldProps> = ({
//   name,
//   checkboxLabel,
//   size,
// }) => {
//   const { values, errors, touched, setValue, setFieldTouched } = useFormContext();
  
//   const fieldError = touched[name] && errors[name];
//   const errorMessage = Array.isArray(fieldError) ? fieldError[0] : fieldError;

//   return (
//     <>
//       <CheckBox
//         label={checkboxLabel}
//         checked={values[name] || false}
//         onChange={(checked: boolean) => {
//           setValue(name, checked);
//           setFieldTouched(name, true);
//         }}
//         size={size}
//       />
//       {errorMessage && (
//         <ErrorText>{errorMessage}</ErrorText>
//       )}
//     </>
//   );
// };

interface DateRangeFieldProps extends BaseFormFieldProps {
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
}

// DateRangeField is temporarily disabled due to CustomDateRangeInput component having different props
// export const DateRangeField: React.FC<DateRangeFieldProps> = ({
//   name,
//   label,
//   placeholder,
//   minDate,
//   maxDate,
//   required,
// }) => {
//   const { values, errors, touched, setValue, setFieldTouched } = useFormContext();
  
//   const fieldError = touched[name] && errors[name];
//   const errorMessage = Array.isArray(fieldError) ? fieldError[0] : fieldError;

//   return (
//     <CustomDateRangeInput
//       placeholder={placeholder || label}
//       value={values[name]}
//       onDateChange={(date: Date | null) => {
//         setValue(name, date);
//         setFieldTouched(name, true);
//       }}
//       minDate={minDate}
//       maxDate={maxDate}
//       error={errorMessage}
//       required={required}
//     />
//   );
// };

// Styled component for error text
import styled from 'styled-components';

const ErrorText = styled.p`
  color: #ff4444;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
`;